import React, { useState, useEffect, useMemo } from 'react';
import { Search, Users, ArrowUpDown, ArrowUp, ArrowDown, Phone, Mail, MapPin, Calendar, Pencil, X, Save } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/utils/api';

const fmtDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ROLE_OPTIONS = [
  { value: 'employee', label: 'Employee' },
  { value: 'outlet_manager', label: 'Outlet Manager' },
  { value: 'business_manager', label: 'Business Manager' },
  { value: 'hr', label: 'HR' },
  { value: 'ceo', label: 'CEO' },
];

const EditModal = ({ employee, outlets, allEmployees, onSave, onClose, saving }) => {
  const [form, setForm] = useState({
    full_name: employee.full_name || '',
    employee_code: employee.employee_code || '',
    email: employee.email || '',
    phone: employee.phone || '',
    designation: employee.designation || '',
    outlet_id: employee.outlet_id || '',
    role: employee.role || 'employee',
    joining_date: employee.joining_date || '',
    attendance_basis: employee.attendance_basis || 'shift',
    reporting_to_id: employee.reporting_to_id || '',
    leave_balances: {
      pl: employee.leave_balances?.pl ?? 0,
      comp_off: employee.leave_balances?.comp_off ?? 0,
      maternity: employee.leave_balances?.maternity ?? 0,
    },
  });

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const setLeave = (key, val) => setForm(prev => ({
    ...prev,
    leave_balances: { ...prev.leave_balances, [key]: parseFloat(val) || 0 }
  }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(employee.id, form);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="edit-modal">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Edit Employee</h2>
            <p className="text-xs text-slate-500 mt-0.5">{employee.employee_code} — Changes save to ShiftPro</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="close-modal">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Basic Info */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Full Name</Label>
              <Input value={form.full_name} onChange={e => set('full_name', e.target.value)} className="mt-1" data-testid="edit-name" />
            </div>
            <div>
              <Label>Employee Code</Label>
              <Input value={form.employee_code} onChange={e => set('employee_code', e.target.value)} className="mt-1" data-testid="edit-code" />
            </div>
            <div>
              <Label>Designation</Label>
              <Input value={form.designation} onChange={e => set('designation', e.target.value)} className="mt-1" data-testid="edit-designation" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} className="mt-1" data-testid="edit-email" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={e => set('phone', e.target.value)} className="mt-1" data-testid="edit-phone" />
            </div>
          </div>

          {/* Org Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Outlet</Label>
              <select value={form.outlet_id} onChange={e => set('outlet_id', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1" data-testid="edit-outlet">
                <option value="">Select Outlet</option>
                {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Role</Label>
              <select value={form.role} onChange={e => set('role', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1" data-testid="edit-role">
                {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Joining Date</Label>
              <Input type="date" value={form.joining_date} onChange={e => set('joining_date', e.target.value)} className="mt-1" data-testid="edit-joining" />
            </div>
            <div>
              <Label>Attendance Basis</Label>
              <select value={form.attendance_basis} onChange={e => set('attendance_basis', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1" data-testid="edit-attendance-basis">
                <option value="shift">Shift</option>
                <option value="time">Time</option>
              </select>
            </div>
          </div>

          <div>
            <Label>Reports To</Label>
            <select value={form.reporting_to_id} onChange={e => set('reporting_to_id', e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1" data-testid="edit-reporting">
              <option value="">None</option>
              {allEmployees.filter(e => e.id !== employee.id).map(e => (
                <option key={e.id} value={e.id}>{e.full_name} ({e.designation || e.role})</option>
              ))}
            </select>
          </div>

          {/* Leave Balances */}
          <div>
            <Label className="text-sm font-semibold">Leave Balances</Label>
            <div className="grid grid-cols-3 gap-4 mt-2">
              <div>
                <Label className="text-xs text-slate-500">PL</Label>
                <Input type="number" step="0.5" value={form.leave_balances.pl} onChange={e => setLeave('pl', e.target.value)} className="mt-1" data-testid="edit-pl" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Comp Off</Label>
                <Input type="number" step="0.5" value={form.leave_balances.comp_off} onChange={e => setLeave('comp_off', e.target.value)} className="mt-1" data-testid="edit-compoff" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Maternity</Label>
                <Input type="number" step="0.5" value={form.leave_balances.maternity} onChange={e => setLeave('maternity', e.target.value)} className="mt-1" data-testid="edit-maternity" />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-edit">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-edit">
              <Save className="h-4 w-4 mr-1.5" />
              {saving ? 'Saving...' : 'Save to ShiftPro'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ShiftProEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [sortKey, setSortKey] = useState('full_name');
  const [sortDir, setSortDir] = useState('asc');
  const [editingEmp, setEditingEmp] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [empRes, outRes] = await Promise.all([
        api.get('/shiftpro/employees'),
        api.get('/shiftpro/outlets'),
      ]);
      setEmployees(Array.isArray(empRes.data) ? empRes.data : []);
      setOutlets(Array.isArray(outRes.data) ? outRes.data : []);
    } catch {
      toast.error('Failed to fetch employee data');
    } finally { setLoading(false); }
  };

  const handleSave = async (userId, formData) => {
    setSaving(true);
    try {
      await api.patch(`/shiftpro/employees/${userId}`, formData);
      toast.success('Employee updated in ShiftPro');
      setEditingEmp(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update employee');
    } finally { setSaving(false); }
  };

  const outletNames = useMemo(() => [...new Set(employees.map(e => e.outlet_name).filter(Boolean))].sort(), [employees]);
  const roles = useMemo(() => [...new Set(employees.map(e => e.role).filter(Boolean))].sort(), [employees]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="h-3 w-3 opacity-30 ml-0.5 inline" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 text-yellow-300 ml-0.5 inline" />
      : <ArrowDown className="h-3 w-3 text-yellow-300 ml-0.5 inline" />;
  };

  const filtered = useMemo(() => {
    let list = employees;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(e =>
        (e.full_name || '').toLowerCase().includes(s) ||
        (e.employee_code || '').toLowerCase().includes(s) ||
        (e.email || '').toLowerCase().includes(s) ||
        (e.phone || '').includes(s)
      );
    }
    if (selectedOutlet) list = list.filter(e => e.outlet_name === selectedOutlet);
    if (selectedRole) list = list.filter(e => e.role === selectedRole);
    return [...list].sort((a, b) => {
      let va = a[sortKey] || '', vb = b[sortKey] || '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [employees, search, selectedOutlet, selectedRole, sortKey, sortDir]);

  const thCls = "px-3 py-2.5 font-semibold cursor-pointer select-none hover:text-yellow-300 transition-colors whitespace-nowrap";

  return (
    <div className="space-y-5" data-testid="shiftpro-employees-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Employees Data</h1>
          <p className="mt-1 text-base text-slate-600">Employee records from ShiftPro — click Edit to modify</p>
        </div>
        <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-1.5">
          <Users className="h-4 w-4 text-violet-600" />
          <span className="text-sm font-semibold text-violet-700">{filtered.length} Employees</span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-end gap-4 flex-wrap">
        <div className="min-w-[220px] flex-1">
          <Label>Search</Label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, Code, Email, Phone..." className="pl-9" data-testid="emp-search" />
          </div>
        </div>
        <div className="min-w-[160px]">
          <Label>Outlet</Label>
          <select value={selectedOutlet} onChange={e => setSelectedOutlet(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1" data-testid="emp-outlet-filter">
            <option value="">All Outlets</option>
            {outletNames.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div className="min-w-[140px]">
          <Label>Role</Label>
          <select value={selectedRole} onChange={e => setSelectedRole(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1" data-testid="emp-role-filter">
            <option value="">All Roles</option>
            {roles.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1).replace('_', ' ')}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <p className="text-slate-500">No employees found.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="emp-table">
          <div className="overflow-auto max-h-[calc(100vh-280px)]">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 z-20">
                <tr className="bg-slate-800 text-white">
                  <th className="px-2 py-2.5 text-center font-semibold sticky top-0 bg-slate-800">Sr</th>
                  <th className={`${thCls} text-left sticky top-0 bg-slate-800`} onClick={() => handleSort('employee_code')}>Code <SortIcon col="employee_code" /></th>
                  <th className={`${thCls} text-left sticky left-0 top-0 z-30 bg-slate-800 min-w-[150px]`} onClick={() => handleSort('full_name')}>Name <SortIcon col="full_name" /></th>
                  <th className={`${thCls} text-center sticky top-0 bg-slate-800`} onClick={() => handleSort('designation')}>Designation <SortIcon col="designation" /></th>
                  <th className={`${thCls} text-center sticky top-0 bg-slate-800`} onClick={() => handleSort('outlet_name')}>Outlet <SortIcon col="outlet_name" /></th>
                  <th className={`${thCls} text-center sticky top-0 bg-slate-800`} onClick={() => handleSort('role')}>Role <SortIcon col="role" /></th>
                  <th className="px-3 py-2.5 font-semibold text-center sticky top-0 bg-slate-800">Contact</th>
                  <th className={`${thCls} text-center sticky top-0 bg-slate-800`} onClick={() => handleSort('joining_date')}>Joining <SortIcon col="joining_date" /></th>
                  <th className="px-3 py-2.5 font-semibold text-center sticky top-0 bg-slate-800">Leave Balance</th>
                  <th className="px-3 py-2.5 font-semibold text-center sticky top-0 bg-slate-800">Reports To</th>
                  <th className="px-2 py-2.5 font-semibold text-center sticky top-0 bg-slate-800">Edit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((emp, idx) => {
                  const lb = emp.leave_balances || {};
                  const isExpanded = expandedId === emp.id;
                  return (
                    <React.Fragment key={emp.id}>
                      <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : emp.id)} data-testid={`emp-row-${idx}`}>
                        <td className="px-2 py-2.5 text-center text-slate-500 font-mono">{idx + 1}</td>
                        <td className="px-3 py-2.5 font-mono text-slate-600">{emp.employee_code}</td>
                        <td className="px-3 py-2.5 text-left whitespace-nowrap sticky left-0 bg-white z-10" style={{ boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)' }}>
                          <span className="font-semibold text-slate-900">{emp.full_name}</span>
                        </td>
                        <td className="px-3 py-2.5 text-center text-slate-700">{emp.designation || '-'}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{emp.outlet_name || '-'}</span>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                            emp.role === 'hr' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                            emp.role === 'business_manager' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                            emp.role === 'ceo' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                            emp.role === 'outlet_manager' ? 'bg-teal-50 text-teal-700 border-teal-200' :
                            'bg-slate-50 text-slate-600 border-slate-200'
                          }`}>
                            {(emp.role || '').replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-center text-[10px] text-slate-500">{emp.phone || '-'}</td>
                        <td className="px-3 py-2.5 text-center font-mono text-slate-600">{fmtDate(emp.joining_date)}</td>
                        <td className="px-3 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">PL: {lb.pl ?? 0}</span>
                            {(lb.comp_off || 0) > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">CO: {lb.comp_off}</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-center text-slate-600 text-[11px]">{emp.reporting_to_name || '-'}</td>
                        <td className="px-2 py-2.5 text-center">
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingEmp(emp); }}
                            className="p-1.5 rounded-md hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 transition-colors"
                            data-testid={`edit-btn-${idx}`}
                            title="Edit employee"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={11} className="px-6 py-4 bg-slate-50 border-b-2 border-slate-200">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                              <div className="flex items-center gap-2">
                                <Mail className="h-3.5 w-3.5 text-slate-400" />
                                <div>
                                  <div className="text-[10px] text-slate-400 uppercase font-medium">Email</div>
                                  <div className="font-medium text-slate-700">{emp.email || '-'}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone className="h-3.5 w-3.5 text-slate-400" />
                                <div>
                                  <div className="text-[10px] text-slate-400 uppercase font-medium">Phone</div>
                                  <div className="font-medium text-slate-700">{emp.phone || '-'}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                <div>
                                  <div className="text-[10px] text-slate-400 uppercase font-medium">Joining Date</div>
                                  <div className="font-medium text-slate-700">{fmtDate(emp.joining_date)}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <MapPin className="h-3.5 w-3.5 text-slate-400" />
                                <div>
                                  <div className="text-[10px] text-slate-400 uppercase font-medium">Attendance Basis</div>
                                  <div className="font-medium text-slate-700">{(emp.attendance_basis || '-').toUpperCase()}</div>
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] text-slate-400 uppercase font-medium">Leave Balances</div>
                                <div className="flex gap-2 mt-1">
                                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">PL: {lb.pl ?? 0}</span>
                                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Comp Off: {lb.comp_off ?? 0}</span>
                                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-pink-50 text-pink-700 border border-pink-200">Maternity: {lb.maternity ?? 0}</span>
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] text-slate-400 uppercase font-medium">Reports To</div>
                                <div className="font-medium text-slate-700 mt-1">{emp.reporting_to_name || 'None'}</div>
                              </div>
                              <div>
                                <div className="text-[10px] text-slate-400 uppercase font-medium">Profile Photo</div>
                                <div className="font-medium text-slate-700 mt-1">{emp.has_profile_photo ? 'Yes' : 'No'}</div>
                              </div>
                              <div>
                                <div className="text-[10px] text-slate-400 uppercase font-medium">Created</div>
                                <div className="font-medium text-slate-700 mt-1">{fmtDate(emp.created_at)}</div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingEmp && (
        <EditModal
          employee={editingEmp}
          outlets={outlets}
          allEmployees={employees}
          onSave={handleSave}
          onClose={() => setEditingEmp(null)}
          saving={saving}
        />
      )}
    </div>
  );
};

export default ShiftProEmployees;
